import React from 'react';
import LcaLayout from 'src/Lca/components/layout';
import Breadcrumbs from 'src/Lca/components/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import {
  ProductsItemsCreated,
  ProductsItemsEmpty,
} from 'src/Lca/components/productsItems';
import PageLoading from 'src/components/PageLoading';
import { useElementsPagination, useLcaOrganization } from 'src/common/hooks';
import { LcaProductMaterial } from '../types';
import {
  Typography,
  InputAdornment,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Box,
} from '@mui/material';
import { ArrowDropDown, Search } from '@mui/icons-material';
import ElementsPagination from 'src/components/Pagination';
import { useLcaProducts } from '../hooks';
import DelayedTextField from 'src/components/DelayedTextField';
import { useNavigate } from 'react-router-dom';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

type Product = {
  data_collection_period: string;
  id: number;
  is_draft: boolean;
  last_updated: string;
  materials: LcaProductMaterial[];
  name: string;
  organization_id: number;
  passport_access_token: string;
};

export default function LCAPageIndexProducts() {
  const { t } = useTranslation('lca');
  const data = [{ name: t('productList'), url: '/lca/products' }];
  const navigate = useNavigate();

  const organization = useLcaOrganization();
  const canWrite = organization.can_write_data;

  const [search, setSearch] = React.useState('');

  const { page, setPage, elementsPerPage, setElementsPerPage } =
    useElementsPagination();

  const { products, count, isLoading } = useLcaProducts(
    organization.id,
    page,
    elementsPerPage,
    search
  );

  const [createMenuAnchorEl, setCreateMenuAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const handleCreateMenuClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setCreateMenuAnchorEl(e.currentTarget);
  const handleCreateProductClick = () => {
    navigate('/lca/products/create');
    setCreateMenuAnchorEl(null);
  };
  const handleUploadProductClick = () => {
    setCreateMenuAnchorEl(null);
  };
  return (
    <LcaLayout>
      <Breadcrumbs data={data} />
      <div
        style={{
          marginTop: '44px',
          marginBottom: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h1">{t('products')}</Typography>

        <Box display="flex" gap="8px">
          <Button
            onClick={() =>
              window.open('https://assets.envirly.pl/LCA.pdf', '_blank')
            }
          >
            <LibraryBooksIcon />
            {t('knowledgeBase')}
          </Button>
          {canWrite && (
            <ButtonGroup>
              <Button onClick={handleCreateProductClick}>
                {t('product.createNew')}
              </Button>
              <Button onClick={handleCreateMenuClick}>
                <ArrowDropDown />
              </Button>
              <Menu
                anchorEl={createMenuAnchorEl}
                open={!!createMenuAnchorEl}
                onClose={() => setCreateMenuAnchorEl(null)}
              >
                <MenuItem disabled onClick={handleUploadProductClick}>
                  {t('product.uploadFromExcel')}
                </MenuItem>
              </Menu>
            </ButtonGroup>
          )}
        </Box>
      </div>
      {canWrite && (
        <Typography variant="subtitle1" color="text.secondary">
          {t('product.createNewInstructions')}
        </Typography>
      )}
      <DelayedTextField
        onDelayedChange={setSearch}
        placeholder={t('product.search')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ width: '360px', my: '24px' }}
      />
      {isLoading ? (
        <PageLoading />
      ) : (
        <>
          {!count && !search && <ProductsItemsEmpty canCreate={canWrite} />}
          {!count && search && (
            <Typography variant="h5" color="text.secondary">
              {t('product.noResults')}
            </Typography>
          )}
          {!!count && (
            <div>
              <div
                style={{
                  display: 'flex',
                  gap: '24px',
                  flexWrap: 'wrap',
                  wordBreak: 'break-word',
                }}
              >
                {products.map((el: Product) => (
                  <ProductsItemsCreated
                    key={el.id}
                    id={el.id}
                    name={el.name}
                    passportAccessToken={el.passport_access_token}
                    period={el.data_collection_period}
                    materials={el.materials}
                    last_edited={el.last_updated}
                    url={`/lca/products/${el.id}`}
                    canDelete={canWrite}
                  />
                ))}
              </div>
              <ElementsPagination
                totalElements={count}
                elementsPerPage={elementsPerPage}
                setElementsPerPage={setElementsPerPage}
                page={page}
                setPage={setPage}
                sx={{ mt: '24px' }}
              />
            </div>
          )}
        </>
      )}
    </LcaLayout>
  );
}
